import { useState, useCallback } from 'react';
import { listContacts } from 'services/contact';

export const useContactList = () => {
  const [contactsList, setContactsList] = useState([]);
  const [contactsListOptions, setContactsListOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const sortContactsAlphabetically = useCallback((contacts) => {
    if (!contacts || !Array.isArray(contacts)) return [];

    return contacts.sort((a, b) => {
      const nameA = a?.first_name || a?.title || '';
      const nameB = b?.first_name || b?.title || '';
      return nameA.toLowerCase().localeCompare(nameB.toLowerCase());
    });
  }, []);

  const generateDropdownOptions = useCallback((contacts) => {
    if (!contacts || !Array.isArray(contacts)) return [];

    return contacts.map((contact) => ({
      label: `${contact?.first_name || ''} ${contact?.middle_name || ''} ${contact?.last_name || ''}`.replace(/\s+/g, ' ').trim(),
      value: contact?.contact_id,
    }));
  }, []);

  const loadContacts = useCallback(
    async ({
      contactType = 'person',
      contactStatus = 'ACTIVE',
      keyWord = '',
      isCourtLocation = '',
      isEventLocation = '',
      pageLimit = 10,
      paginationToken = '',
      paginationDirection = 'forward',
      inLead = false,
    } = {}) => {
      try {
        setLoading(true);
        setError(null);

        const limit = pageLimit;

        const response = await listContacts(
          contactType,
          '',
          contactStatus,
          keyWord,
          isCourtLocation,
          isEventLocation,
          '',
          limit,
          paginationToken,
          paginationDirection,
          inLead
        );

        const responseData = response?.data?.contacts;

        if (responseData?.length > 0) {
          const sortedContacts = responseData.sort((a, b) => {
            const nameA = a?.first_name || a?.title || '';
            const nameB = b?.first_name || b?.title || '';
            return nameA.toLowerCase().localeCompare(nameB.toLowerCase());
          });

          const dropdownOptions = sortedContacts.map((contact) => ({
            label: `${contact?.first_name || ''} ${contact?.middle_name || ''} ${contact?.last_name || ''}`.replace(/\s+/g, ' ').trim(),
            value: contact?.contact_id,
          }));

          setContactsList(sortedContacts);
          setContactsListOptions(dropdownOptions);
        } else {
          setContactsList([]);
          setContactsListOptions([]);
        }
      } catch (err) {
        console.log(err);
        setError(err);
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const refreshContacts = useCallback(
    (params = {}) => {
      loadContacts(params);
    },
    [loadContacts]
  );

  const loadInitialContacts = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await listContacts('person', '', 'ACTIVE', '', '', '', '', 10, '', 'forward', false);

      const responseData = response?.data?.contacts;

      if (responseData?.length > 0) {
        const sortedContacts = responseData.sort((a, b) => {
          const nameA = a?.first_name || a?.title || '';
          const nameB = b?.first_name || b?.title || '';
          return nameA.toLowerCase().localeCompare(nameB.toLowerCase());
        });

        const dropdownOptions = sortedContacts.map((contact) => ({
          label: `${contact?.first_name || ''} ${contact?.middle_name || ''} ${contact?.last_name || ''}`.replace(/\s+/g, ' ').trim(),
          value: contact?.contact_id,
          client: contact,
        }));

        setContactsList(sortedContacts);
        setContactsListOptions(dropdownOptions);

        return dropdownOptions;
      } else {
        setContactsList([]);
        setContactsListOptions([]);
        return [];
      }
    } catch (err) {
      console.log(err);
      setError(err);
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  const searchContacts = useCallback(async (query) => {
    if (!query || query.length < 1) {
      return [];
    }

    try {
      const response = await listContacts('person', '', 'ACTIVE', query, '', '', '', 10, '', 'forward', false);

      const responseData = response?.data?.contacts;

      if (responseData?.length > 0) {
        // Sort contacts alphabetically inline
        const sortedContacts = responseData.sort((a, b) => {
          const nameA = a?.first_name || a?.title || '';
          const nameB = b?.first_name || b?.title || '';
          return nameA.toLowerCase().localeCompare(nameB.toLowerCase());
        });

        // Generate dropdown options inline
        return sortedContacts.map((contact) => ({
          label: `${contact?.first_name || ''} ${contact?.middle_name || ''} ${contact?.last_name || ''}`.replace(/\s+/g, ' ').trim(),
          value: contact?.contact_id,
          client: contact, // Include full contact object for AutoComplete
        }));
      }

      return [];
    } catch (err) {
      console.log(err);
      return [];
    }
  }, []);

  const searchContactsWithFullObjects = useCallback(async (query) => {
    if (!query || query.length < 1) {
      return [];
    }

    try {
      const response = await listContacts('person', '', 'ACTIVE', query, '', '', '', 10, '', 'forward', false);

      const responseData = response?.data?.contacts;

      if (responseData?.length > 0) {
        const sortedContacts = responseData.sort((a, b) => {
          const nameA = a?.first_name || a?.title || '';
          const nameB = b?.first_name || b?.title || '';
          return nameA.toLowerCase().localeCompare(nameB.toLowerCase());
        });

        return sortedContacts.map((contact) => ({
          label: `${contact?.first_name || ''} ${contact?.middle_name || ''} ${contact?.last_name || ''}`.replace(/\s+/g, ' ').trim(),
          value: contact?.contact_id,
          client: contact,
        }));
      }

      return [];
    } catch (err) {
      console.log(err);
      return [];
    }
  }, []);

  const searchCourtLocationsWithFullObjects = useCallback(async (query) => {
    if (!query || query.length < 2) {
      return [];
    }

    try {
      const response = await listContacts('location', '', 'ACTIVE', query, true, '', '', 10, '', 'forward', false);

      const responseData = response?.data?.contacts;

      if (responseData?.length > 0) {
        const sortedCourts = responseData.sort((a, b) => (a?.title || '').localeCompare(b?.title || ''));
        return sortedCourts.map((court) => ({
          label: court?.title || '',
          value: court?.contact_id,
        }));
      }

      return [];
    } catch (err) {
      console.log(err);
      return [];
    }
  }, []);

  const findContactById = useCallback(
    (contactId) => {
      return contactsList.find((contact) => contact.contact_id === contactId);
    },
    [contactsList]
  );

  // Get contact display name
  const getContactDisplayName = useCallback((contact) => {
    if (!contact) return '';
    return `${contact?.first_name || ''} ${contact?.middle_name || ''} ${contact?.last_name || ''}`.replace(/\s+/g, ' ').trim();
  }, []);

  return {
    // Data
    contactsList,
    contactsListOptions,
    loading,
    error,

    // Actions
    loadContacts,
    loadInitialContacts,
    refreshContacts,
    searchContacts,
    searchContactsWithFullObjects,
    searchCourtLocationsWithFullObjects,
    findContactById,
    getContactDisplayName,

    // Utilities
    sortContactsAlphabetically,
    generateDropdownOptions,
    setContactsList,
    setContactsListOptions,
  };
};
